import { defineStore } from 'pinia'
import { getIndexerSpotApi } from '@shared/service'
import { combineOrderbookRecords } from '@/app/utils/market'
import { TRADE_MAX_SUBACCOUNT_ARRAY_SIZE } from '@/app/utils/constants'
import type {
  SpotLimitOrder,
  SpotAverageEntry,
  SpotOrderHistory
} from '@injectivelabs/sdk-ts'
import type {
  OrderSide,
  TradeExecutionSide,
  TradeExecutionType
} from '@injectivelabs/sdk-ts/types'
import type {
  SharedUiSpotTrade,
  SharedUiSpotMarket,
  SharedUiOrderbookWithSequence
} from '@shared/types'
import type { UiMarketAndSummary, ActivityFetchOptions } from '@/types'

type SpotStoreState = {
  trades: SharedUiSpotTrade[]
  marketIdsFromQuery: string[]
  subaccountTradesCount: number
  subaccountOrdersCount: number
  subaccountOrders: SpotLimitOrder[]
  subaccountOrdersOffsetCount: number
  subaccountTradesOffsetCount: number
  subaccountOrderHistoryCount: number
  subaccountTrades: SharedUiSpotTrade[]
  subaccountOrderHistoryOffsetCount: number
  orderbook?: SharedUiOrderbookWithSequence
  subaccountOrderHistory: SpotOrderHistory[]
  accountAverageEntries: Record<string, SpotAverageEntry>
}

const initialStateFactory = (): SpotStoreState => ({
  marketIdsFromQuery: [],
  orderbook: undefined,
  trades: [],
  subaccountTrades: [] as SharedUiSpotTrade[],
  subaccountTradesCount: 0,
  subaccountTradesOffsetCount: 0,
  subaccountOrders: [] as SpotLimitOrder[],
  subaccountOrdersCount: 0,
  subaccountOrdersOffsetCount: 0,
  subaccountOrderHistory: [] as SpotOrderHistory[],
  subaccountOrderHistoryCount: 0,
  subaccountOrderHistoryOffsetCount: 0,
  accountAverageEntries: {}
})

export const useSpotStore = defineStore('spot', {
  state: (): SpotStoreState => initialStateFactory(),
  getters: {
    buys: (state) => state.orderbook?.buys || [],
    sells: (state) => state.orderbook?.sells || [],

    marketByIdOrSlug:
      () =>
      (marketIdOrSlug: string): undefined | SharedUiSpotMarket => {
        const sharedSpotStore = useSharedSpotStore()

        const market = sharedSpotStore.marketsWithToken.find(
          (market) =>
            market.marketId === marketIdOrSlug || market.slug === marketIdOrSlug
        )

        return market as SharedUiSpotMarket
      },

    activeMarketIds: (state) => {
      const jsonStore = useSharedJsonStore()
      const sharedSpotStore = useSharedSpotStore()

      return sharedSpotStore.marketsWithToken
        .filter(
          ({ marketId }) =>
            state.marketIdsFromQuery.includes(marketId) ||
            jsonStore.verifiedSpotMarketIds.includes(marketId)
        )
        .map((m) => m.marketId)
    },

    tradableDenoms: () => {
      const sharedSpotStore = useSharedSpotStore()

      return [
        ...sharedSpotStore.marketsWithToken.reduce((denoms, market) => {
          if (!market.isVerified) {
            return denoms
          }

          denoms.add(market.baseDenom)
          denoms.add(market.quoteDenom)

          return denoms
        }, new Set() as Set<string>)
      ]
    },

    unverifiedDenoms: () => {
      const sharedSpotStore = useSharedSpotStore()

      return [
        ...sharedSpotStore.marketsWithToken.reduce((denoms, market) => {
          if (market.isVerified) {
            return denoms
          }

          denoms.add(market.baseDenom)
          denoms.add(market.quoteDenom)

          return denoms
        }, new Set() as Set<string>)
      ]
    },

    marketsWithSummary: () => {
      const sharedSpotStore = useSharedSpotStore()

      return sharedSpotStore.marketsWithToken
        .map((market) => ({
          market,
          summary: sharedSpotStore.marketsSummary.find(
            (summary) => summary.marketId === market.marketId
          )
        }))
        .filter(({ summary }) => summary) as UiMarketAndSummary[]
    }
  },
  actions: {
    // Lazy-loaded message actions
    submitSpotChase: lazyPiniaAction(
      () => import('./message'),
      'submitSpotChase'
    ),
    cancelSpotOrder: lazyPiniaAction(
      () => import('./message'),
      'cancelSpotOrder'
    ),
    submitSpotLimitOrder: lazyPiniaAction(
      () => import('./message'),
      'submitSpotLimitOrder'
    ),
    batchCancelSpotOrder: lazyPiniaAction(
      () => import('./message'),
      'batchCancelSpotOrder'
    ),
    submitSpotMarketOrder: lazyPiniaAction(
      () => import('./message'),
      'submitSpotMarketOrder'
    ),

    // Lazy-loaded stream actions
    streamSpotTrades: lazyPiniaAction(
      () => import('./stream'),
      'streamSpotTrades'
    ),
    streamSubaccountSpotOrders: lazyPiniaAction(
      () => import('./stream'),
      'streamSubaccountSpotOrders'
    ),
    streamSubaccountSpotTradesHistory: lazyPiniaAction(
      () => import('./stream'),
      'streamSubaccountSpotTradesHistory'
    ),
    streamAccountAverageEntries: lazyPiniaAction(
      () => import('./stream'),
      'streamAccountAverageEntries'
    ),
    streamSubaccountSpotOrderHistory: lazyPiniaAction(
      () => import('./stream'),
      'streamSubaccountSpotOrderHistory'
    ),

    // Stream cancel functions
    cancelSpotTradesStream: lazyPiniaAction(
      () => import('./stream'),
      'cancelSpotTradesStream'
    ),
    cancelAccountAverageEntriesStream: lazyPiniaAction(
      () => import('./stream'),
      'cancelAccountAverageEntriesStream'
    ),
    cancelSubaccountSpotOrdersStream: lazyPiniaAction(
      () => import('./stream'),
      'cancelSubaccountSpotOrdersStream'
    ),
    cancelSubaccountSpotTradesHistoryStream: lazyPiniaAction(
      () => import('./stream'),
      'cancelSubaccountSpotTradesHistoryStream'
    ),
    cancelSubaccountSpotOrderHistoryStream: lazyPiniaAction(
      () => import('./stream'),
      'cancelSubaccountSpotOrderHistoryStream'
    ),

    async appendMarketId(marketIdFromQuery: string) {
      const spotStore = useSpotStore()

      spotStore.marketIdsFromQuery = [
        ...spotStore.marketIdsFromQuery,
        marketIdFromQuery
      ]
    },

    async fetchSubaccountOrders(marketIds?: string[]) {
      const indexerSpotApi = await getIndexerSpotApi()

      const spotStore = useSpotStore()
      const accountStore = useAccountStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected || !accountStore.subaccountId) {
        return
      }

      const { orders, pagination } = await indexerSpotApi.fetchOrders({
        marketIds,
        subaccountId: accountStore.subaccountId,
        pagination: {
          limit: TRADE_MAX_SUBACCOUNT_ARRAY_SIZE
        }
      })

      spotStore.$patch({
        subaccountOrders: orders,
        subaccountOrdersCount: pagination.total,
        subaccountOrdersOffsetCount: Math.max(
          0,
          pagination.total - orders.length
        )
      })
    },

    async fetchSubaccountOrderHistory(options?: ActivityFetchOptions) {
      const indexerSpotApi = await getIndexerSpotApi()

      const spotStore = useSpotStore()
      const accountStore = useAccountStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!(sharedWalletStore.isUserConnected && accountStore.subaccountId)) {
        return
      }

      const filters = options?.filters

      const { orderHistory, pagination } =
        await indexerSpotApi.fetchOrderHistory({
          subaccountId: options?.subaccountId || accountStore.subaccountId,
          direction: filters?.direction,
          pagination: options?.pagination,
          isConditional: filters?.isConditional,
          marketIds: filters?.marketIds || spotStore.activeMarketIds,
          orderTypes: filters?.orderTypes as unknown as OrderSide[],
          executionTypes: filters?.executionTypes as TradeExecutionType[]
        })

      spotStore.$patch({
        subaccountOrderHistory: orderHistory,
        subaccountOrderHistoryCount: pagination.total,
        subaccountOrderHistoryOffsetCount: Math.max(
          0,
          pagination.total - orderHistory.length
        )
      })
    },

    async fetchBotSubaccountOrderHistory(options: ActivityFetchOptions) {
      const indexerSpotApi = await getIndexerSpotApi()

      const spotStore = useSpotStore()

      if (!options?.subaccountId) {
        return
      }

      const filters = options.filters

      const { orderHistory, pagination } =
        await indexerSpotApi.fetchOrderHistory({
          subaccountId: options.subaccountId,
          direction: filters?.direction,
          pagination: options.pagination,
          isConditional: filters?.isConditional,
          marketIds: filters?.marketIds || spotStore.activeMarketIds,
          orderTypes: filters?.orderTypes as unknown as OrderSide[],
          executionTypes: filters?.executionTypes as TradeExecutionType[]
        })

      spotStore.$patch({
        subaccountOrderHistory: orderHistory,
        subaccountOrderHistoryCount: pagination.total,
        subaccountOrderHistoryOffsetCount: Math.max(
          0,
          pagination.total - orderHistory.length
        )
      })
    },

    async fetchSubaccountTradesHistory(options?: ActivityFetchOptions) {
      const indexerSpotApi = await getIndexerSpotApi()

      const spotStore = useSpotStore()
      const accountStore = useAccountStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!(sharedWalletStore.isUserConnected && accountStore.subaccountId)) {
        return
      }

      const filters = options?.filters

      const { trades, pagination } = await indexerSpotApi.fetchTrades({
        subaccountId: options?.subaccountId || accountStore.subaccountId,
        direction: filters?.direction,
        pagination: options?.pagination,
        marketIds: filters?.marketIds || spotStore.activeMarketIds,
        executionTypes: filters?.executionTypes as TradeExecutionType[]
      })

      spotStore.$patch({
        subaccountTrades: trades,
        subaccountTradesCount: pagination.total,
        subaccountTradesOffsetCount: Math.max(
          0,
          pagination.total - trades.length
        )
      })
    },

    async fetchOrderbook(marketId: string) {
      const indexerSpotApi = await getIndexerSpotApi()

      const spotStore = useSpotStore()

      const currentOrderbookSequence = spotStore.orderbook?.sequence || 0
      const latestOrderbook = await indexerSpotApi.fetchOrderbookV2(marketId)
      const latestOrderbookIsMostRecent =
        latestOrderbook.sequence >= currentOrderbookSequence

      if (latestOrderbookIsMostRecent) {
        spotStore.orderbook = latestOrderbook
      }

      // handle race condition between fetch and stream
      spotStore.orderbook = {
        sequence: latestOrderbookIsMostRecent
          ? latestOrderbook.sequence
          : currentOrderbookSequence,
        buys: combineOrderbookRecords({
          isBuy: true,
          currentRecords: spotStore.orderbook?.buys,
          updatedRecords: latestOrderbook.buys
        }),
        sells: combineOrderbookRecords({
          isBuy: false,
          currentRecords: spotStore.orderbook?.sells,
          updatedRecords: latestOrderbook.sells
        })
      }
    },

    async fetchTrades({
      marketId,
      executionSide
    }: {
      marketId: string
      executionSide?: TradeExecutionSide
    }) {
      const indexerSpotApi = await getIndexerSpotApi()

      const spotStore = useSpotStore()

      const { trades } = await indexerSpotApi.fetchTrades({
        marketIds: [marketId],
        executionSide
      })

      spotStore.trades = trades
    },

    async fetchLastTrade({
      marketId,
      executionSide
    }: {
      marketId: string
      executionSide?: TradeExecutionSide
    }) {
      const indexerSpotApi = await getIndexerSpotApi()

      const { trades } = await indexerSpotApi.fetchTrades({
        marketIds: [marketId],
        executionSide,
        pagination: { limit: 1 }
      })

      return trades[0]
    },

    async fetchBotSubaccountOrders({
      subaccountId,
      marketIds
    }: {
      marketIds: string[]
      subaccountId: string
    }) {
      const indexerSpotApi = await getIndexerSpotApi()

      const spotStore = useSpotStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected || !subaccountId) {
        return
      }

      const { orders, pagination } = await indexerSpotApi.fetchOrders({
        subaccountId,
        marketIds: marketIds || spotStore.activeMarketIds,
        pagination: {
          limit: TRADE_MAX_SUBACCOUNT_ARRAY_SIZE
        }
      })

      spotStore.$patch({
        subaccountOrders: orders,
        subaccountOrdersCount: pagination.total,
        subaccountOrdersOffsetCount: Math.max(
          0,
          pagination.total - orders.length
        )
      })
    },

    async fetchBotSubaccountTrades(options: ActivityFetchOptions) {
      const indexerSpotApi = await getIndexerSpotApi()

      const spotStore = useSpotStore()

      if (!options?.subaccountId) {
        return
      }

      const filters = options.filters

      const { trades, pagination } = await indexerSpotApi.fetchTrades({
        subaccountId: options.subaccountId,
        direction: filters?.direction,
        pagination: options.pagination,
        marketIds: filters?.marketIds || spotStore.activeMarketIds,
        executionTypes: filters?.executionTypes as TradeExecutionType[]
      })

      spotStore.$patch({
        subaccountTrades: trades,
        subaccountTradesCount: pagination.total
      })
    },

    setAccountAverageEntry(averageEntry: SpotAverageEntry) {
      this.accountAverageEntries[averageEntry.marketId] = averageEntry
    },

    deleteAccountAverageEntry(marketId: string) {
      delete this.accountAverageEntries[marketId]
    },

    resetAccountAverageEntries() {
      this.accountAverageEntries = {}
    },

    async cancelSubaccountStream() {
      const spotStore = useSpotStore()

      await Promise.all([
        spotStore.cancelSubaccountSpotOrdersStream(),
        spotStore.cancelSubaccountSpotOrderHistoryStream(),
        spotStore.cancelSubaccountSpotTradesHistoryStream()
      ])
    },

    resetOrderbookAndTrades() {
      const spotStore = useSpotStore()

      spotStore.$patch({
        trades: [],
        orderbook: undefined
      })
    },

    async resetSubaccount() {
      const spotStore = useSpotStore()

      const {
        subaccountOrders,
        subaccountTrades,
        subaccountOrdersCount,
        subaccountTradesCount,
        subaccountOrderHistory,
        subaccountOrderHistoryCount
      } = initialStateFactory()

      await spotStore.cancelSubaccountStream()

      spotStore.$patch({
        subaccountOrders,
        subaccountTrades,
        subaccountTradesCount,
        subaccountOrdersCount,
        subaccountOrderHistory,
        subaccountOrderHistoryCount
      })
    },

    reset() {
      const spotStore = useSpotStore()

      const {
        trades,
        orderbook,
        subaccountOrders,
        subaccountTrades,
        accountAverageEntries
      } = initialStateFactory()

      spotStore.$patch({
        trades,
        orderbook,
        subaccountOrders,
        subaccountTrades,
        accountAverageEntries
      })
    }
  }
})
